import { FC, SVGProps } from 'react'

import { object as yupObject, string as yupString } from 'yup'

import GptIcon from 'src/Assets/Svg/survey/GPT.svg?react'
import ClaudeIcon from 'src/Assets/Svg/survey/Claude.svg?react'
import GeminiIcon from 'src/Assets/Svg/survey/Gemini.svg?react'
import GrokIcon from 'src/Assets/Svg/survey/Grok.svg?react'
import PerplexityIcon from 'src/Assets/Svg/survey/Perplexity.svg?react'
import OtherIcon from 'src/Assets/Svg/survey/Other.svg?react'
import GoogleIcon from 'src/Assets/Svg/survey/Google.svg?react'
import YouTubeIcon from 'src/Assets/Svg/survey/YouTube.svg?react'
import AdIcon from 'src/Assets/Svg/survey/Ad.svg?react'
import TikTokIcon from 'src/Assets/Svg/survey/TikTok.svg?react'
import InstagramIcon from 'src/Assets/Svg/survey/Inst.svg?react'
import FacebookIcon from 'src/Assets/Svg/survey/Facebook.svg?react'
import TwitterIcon from 'src/Assets/Svg/survey/Twitter.svg?react'
import RedditIcon from 'src/Assets/Svg/survey/Vector.svg?react'
import LinkedInIcon from 'src/Assets/Svg/survey/LinkedIn.svg?react'
import BlogIcon from 'src/Assets/Svg/survey/Blog.svg?react'
import PodcastIcon from 'src/Assets/Svg/survey/Podcast.svg?react'
import EventIcon from 'src/Assets/Svg/survey/Event.svg?react'
import ArtistIcon from 'src/Assets/Svg/survey/Artist.svg?react'
import FriendsIcon from 'src/Assets/Svg/survey/Friends.svg?react'
import MailIcon from 'src/Assets/Svg/survey/Mail.svg?react'

import { SurveyItemProps } from './SurveyItem'

export type SvgIcon = FC<SVGProps<SVGSVGElement>>

export interface SurveyFormValues {
  resource: string
  description?: string
}

export interface SurveyGroup {
  title: string
  items: SurveyItemProps[]
}

const OTHER_VARIANT = 'Other'
const OTHER_GROUP_TITLE = 'Other'
const PROFILE_NAME_PLACEHOLDER = 'Profile name (Optional)'
const PODCAST_NAME_PLACEHOLDER = 'Podcast name (Optional)'
const PLEASE_SPECIFY = 'Please specify how you heard about us'

const OTHER_GROUP: SurveyGroup = {
  title: OTHER_GROUP_TITLE,
  items: [
    { title: 'Friend/colleague', Icon: FriendsIcon },
    { title: 'Email/newsletter', Icon: MailIcon },
    {
      title: OTHER_VARIANT,
      Icon: OtherIcon,
      placeholder: PLEASE_SPECIFY,
      inputType: 'textarea',
    },
  ],
}

const NON_OTHER_GROUPS: SurveyGroup[] = [
  {
    title: 'AI Assistant',
    items: [
      { title: 'ChatGPT', Icon: GptIcon },
      { title: 'Claude', Icon: ClaudeIcon },
      { title: 'Gemini', Icon: GeminiIcon },
      { title: 'Grok', Icon: GrokIcon },
      { title: 'Perplexity', Icon: PerplexityIcon },
      { title: 'Other AI assistant', Icon: OtherIcon },
    ],
  },
  {
    title: 'Search & Web',
    items: [
      { title: 'Google Search', Icon: GoogleIcon },
      {
        title: 'YouTube',
        Icon: YouTubeIcon,
        placeholder: PROFILE_NAME_PLACEHOLDER,
        inputType: 'input',
      },
      { title: 'Online web ad', Icon: AdIcon },
    ],
  },
  {
    title: 'Social Media',
    items: [
      {
        title: 'TikTok',
        Icon: TikTokIcon,
        placeholder: PROFILE_NAME_PLACEHOLDER,
        inputType: 'input',
      },
      {
        title: 'Instagram',
        Icon: InstagramIcon,
        placeholder: PROFILE_NAME_PLACEHOLDER,
        inputType: 'input',
      },
      {
        title: 'Facebook',
        Icon: FacebookIcon,
        placeholder: PROFILE_NAME_PLACEHOLDER,
        inputType: 'input',
      },
      {
        title: 'X (Twitter)',
        Icon: TwitterIcon,
        placeholder: PROFILE_NAME_PLACEHOLDER,
        inputType: 'input',
      },
      { title: 'Reddit', Icon: RedditIcon },
      {
        title: 'LinkedIn',
        Icon: LinkedInIcon,
        placeholder: PROFILE_NAME_PLACEHOLDER,
        inputType: 'input',
      },
    ],
  },
  {
    title: 'Music Industry',
    items: [
      { title: 'Blog/publication', Icon: BlogIcon },
      {
        title: 'Podcast',
        Icon: PodcastIcon,
        placeholder: PODCAST_NAME_PLACEHOLDER,
        inputType: 'input',
      },
      { title: 'Conference/event', Icon: EventIcon },
      { title: 'Artist/label recommendation', Icon: ArtistIcon },
    ],
  },
]

function getRandomIndex(max: number): number {
  const buffer = new Uint32Array(1)
  crypto.getRandomValues(buffer)
  return buffer[0] % (max + 1)
}

function fisherYatesShuffle<T>(array: T[]): T[] {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = getRandomIndex(i)
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

const isOtherItem = (item: SurveyItemProps): boolean =>
  item.title.includes(OTHER_VARIANT)

const shuffleItemsWithOtherLast = (
  items: SurveyItemProps[],
): SurveyItemProps[] => {
  const otherItems = items.filter(isOtherItem)
  const regularItems = items.filter((item) => !isOtherItem(item))

  return [...fisherYatesShuffle(regularItems), ...otherItems]
}
export function shuffleSurveyGroups(): SurveyGroup[] {
  const shuffledGroups = fisherYatesShuffle(NON_OTHER_GROUPS).map((group) => ({
    ...group,
    items: shuffleItemsWithOtherLast(group.items),
  }))

  return [
    ...shuffledGroups,
    {
      ...OTHER_GROUP,
      items: shuffleItemsWithOtherLast(OTHER_GROUP.items),
    },
  ]
}

export const surveyFormSchema = yupObject().shape({
  resource: yupString().required(),
  description: yupString()
    .trim()
    .max(100)
    .when('resource', (value: string[], schema) => {
      if (value[0] === OTHER_VARIANT) {
        return schema.required(PLEASE_SPECIFY).min(1, PLEASE_SPECIFY)
      }
      return schema
    }),
})
